/* eslint-disable */
import saveStoreDraft from "../../../utils/saveStoreDraft";
import useStoreFormDraft from "../../../hooks/useStoreFormDraft";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { applyStoreDraft } from "../../../redux/store/store";
import { doc } from "firebase/firestore";
import { db } from "../../../firebase";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import apicache from "../../../components/clearCach/clear";
import {
  Bell,
  Check,
  X,
  MessageCircle,
  Settings,
  Info,
  RefreshCw,
  Send,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ActivationTelegram({ close }) {
  const store = useSelector((state) => state.StoreReducer);
  const dispatch = useDispatch();
  const MySwal = withReactContent(Swal);
  const [loading, setLoading] = useState(false);
  const products = useSelector((state) => state.ProductsReducer);
  const account = useSelector((state) => state.AccountReducer);
  const [isTelegramActive, setIsTelegramActive] = useState(false);
  const [initialState, setInitialState] = useState(null);
  const { t } = useTranslation();

  const [data, setData] = useStoreFormDraft(store);

  useEffect(() => {
    // Initialize data from store
    if (store) {

      setInitialState({ ...store });

      // Check if Telegram is active (has ID and at least one notification type enabled)
      const hasValidId = store.Idtelegram && /^\d{7,12}$/.test(store.Idtelegram);
      const hasEnabledNotification =
        store.Tdone ||
        store.Tconfrim ||
        store.Tcancel ||
        store.Ttermine ||
        store.Tdelevery ||
        store.Tnew;
      setIsTelegramActive(hasValidId && hasEnabledNotification);
    }
  }, [store]);

  // Toggle handlers for notification types
  const toggleNotificationType = (key) => {
    setData({ ...data, [key]: !data[key] });
  };

  const clearTelegramId = () => {
    setData({ ...data, Idtelegram: "" });
  };

  const disableAllNotifications = () => {
    setData({
      ...data,
      Tdone: false,
      Tconfrim: false,
      Tcancel: false,
      Ttermine: false,
      Tdelevery: false,
      Tnew: false,
    });
  };

  const showAlert = (
    icon,
    title,
    text,
    showCancelButton = false,
    confirmButtonText = "حسناً"
  ) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText,
      cancelButtonText: t("annule"),
      showCancelButton,
      confirmButtonColor: "rgb(141, 93, 167)",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      backdrop: `
        rgba(0,0,0,0.7)
      `,
      position: "center",
      customClass: {
        popup: "swal-popup-on-top",
        backdrop: "swal-backdrop-on-top",
        container: "swal-container-on-top",
      },
      willOpen: () => {
        // Ensure this alert appears on top of everything
        document.querySelector(".swal2-container").style.zIndex = "9999999999";
      },
    });
  };

  // Check if state has changed
  const hasStateChanged = () => {
    if (!initialState) return false;

    // Check if ID has changed
    if (data.Idtelegram !== initialState.Idtelegram) return true;

    // Check if any notification setting has changed
    const notificationKeys = [
      "Tdone",
      "Tconfrim",
      "Tcancel",
      "Ttermine",
      "Tdelevery",
      "Tnew",
    ];
    for (const key of notificationKeys) {
      if (data[key] !== initialState[key]) return true;
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if state has changed
    if (!hasStateChanged()) {
      await showAlert(
        "info",
        t("servicePageActivation.telegram.alerts.noChanges.title"),
        t("servicePageActivation.telegram.alerts.noChanges.text")
      );
      return;
    }

    // Check if we're activating or deactivating
    const hasValidId = data.Idtelegram && /^\d{7,12}$/.test(data.Idtelegram);
    const hasEnabledNotification =
      data.Tdone ||
      data.Tconfrim ||
      data.Tcancel ||
      data.Ttermine ||
      data.Tdelevery ||
      data.Tnew;

    const wasActive = isTelegramActive;
    const isActivating = hasValidId && hasEnabledNotification && !wasActive;
    const isDeactivating =
      (!hasValidId || !hasEnabledNotification) && wasActive;
    const isUpdating = hasValidId && hasEnabledNotification && wasActive;

    // If activating or updating, validate the input
    if (isActivating || isUpdating) {
      // Form validation: Check if the ID is correctly filled (10 digits)
      if (!/^\d{7,12}$/.test(data.Idtelegram)) {
        await showAlert(
          "warning",
          t("servicePageActivation.telegram.alerts.invalidId.title"),
          t("servicePageActivation.telegram.alerts.invalidId.text")
        );
        return;
      }

      // Validate that at least one notification type is selected
      if (!hasEnabledNotification) {
        await showAlert(
          "warning",
          t("servicePageActivation.telegram.alerts.noNotificationSelected.title"),
          t("servicePageActivation.telegram.alerts.noNotificationSelected.text")
        );
        return;
      }
    } else if (isDeactivating) {
      // If deactivating, confirm with user
      const result = await showAlert(
        "warning",
        t("servicePageActivation.telegram.alerts.deactivateConfirm.title"),
        t("servicePageActivation.telegram.alerts.deactivateConfirm.text"),
        true,
        t("servicePageActivation.telegram.alerts.deactivateConfirm.confirmButtonText")
      );

      // If user cancels the confirmation, stop the process
      if (!result.isConfirmed) {
        // Reset to initial state
        setData(initialState);
        return;
      }
    }

    try {
      setLoading(true);

      await apicache(account, products);
      await saveStoreDraft(doc(db, "store", data.id), store, data);
      dispatch(applyStoreDraft(store, data));

      // Update state
      setLoading(false);
      setInitialState({ ...data });

      // Update activation status
      const newActiveStatus = hasValidId && hasEnabledNotification;
      setIsTelegramActive(newActiveStatus);

      if (isDeactivating) {
        await showAlert("success", t("servicePageActivation.telegram.alerts.deactivationSuccess.title"), "");
      } else if (isActivating) {
        await showAlert("success", t("servicePageActivation.telegram.alerts.activationSuccess.title"), "");
      } else {
        await showAlert("success", t("servicePageActivation.telegram.alerts.saveSuccess.title"), "");
      }
      close();
    } catch (error) {
      setLoading(false);

      if (isDeactivating) {
        await showAlert(
          "error",
          t("servicePageActivation.telegram.alerts.deactivationError.title"),
          t("servicePageActivation.telegram.alerts.deactivationError.text")
        );
      } else {
        await showAlert(
          "error",
          t("servicePageActivation.telegram.alerts.deactivationError.title"),
          t("servicePageActivation.telegram.alerts.saveError.text")
        );
      }

      console.error("Error submitting form:", error);
    }
  };

  // Notification settings configuration
  const notificationItems = [
    {
      key: "Tnew",
      label: t("servicePageActivation.telegram.notifications.Tnew.label"),
      badge: "New",
      icon: <Bell size={16} className="ml-2" />,
    },
    {
      key: "Tdone",
      label: t("servicePageActivation.telegram.notifications.Tdone.label"),
      badge: "Done",
      icon: <Check size={16} className="ml-2" />,
    },
    {
      key: "Tconfrim",
      label: t("servicePageActivation.telegram.notifications.Tconfrim.label"),
      badge: "Confirmed",
      icon: <Check size={16} className="ml-2" />,
    },
    {
      key: "Tcancel",
      label: t("servicePageActivation.telegram.notifications.Tcancel.label"),
      badge: "Canceled",
      icon: <X size={16} className="ml-2" />,
    },
    {
      key: "Ttermine",
      label: t("servicePageActivation.telegram.notifications.Ttermine.label"),
      badge: "Pending Confirmation",
      icon: <Info size={16} className="ml-2" />,
    },
    {
      key: "Tdelevery",
      label: t("servicePageActivation.telegram.notifications.Tdelevery.label"),
      badge: "At Delivery Company",
      icon: <MessageCircle size={16} className="ml-2" />,
    },
  ];

  // Add this style to ensure SweetAlert appears on top
  useEffect(() => {
    // Add CSS to make sure SweetAlert appears on top of other modals
    const style = document.createElement("style");
    style.innerHTML = `
      .swal-container-on-top {
        z-index: 9999999999 !important;
      }
      .swal-backdrop-on-top {
        background: rgba(0,0,0,0.7) !important;
      }
      .swal-popup-on-top {
        position: relative;
        box-shadow: 0 0 20px rgba(0,0,0,0.2);
      }
      body.swal2-shown {
        overflow: hidden;
      }
      body.swal2-shown > [aria-hidden="true"] {
        filter: blur(3px);
      }
    `;
    document.head.appendChild(style);

    // Add a click handler to ensure modal shows on top
    const handleDocumentClick = () => {
      const swalContainer = document.querySelector(".swal2-container");
      if (swalContainer) {
        swalContainer.style.zIndex = "9999999999";
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.head.removeChild(style);
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  // Check if any notification types are enabled
  const hasEnabledNotifications =
    data.Tdone ||
    data.Tconfrim ||
    data.Tcancel ||
    data.Ttermine ||
    data.Tdelevery ||
    data.Tnew;

  // Check if ID is valid
  const hasValidId = data.Idtelegram && /^\d{7,12}$/.test(data.Idtelegram);

  // Determine button state
  const isFormValid = hasValidId && hasEnabledNotifications;
  const isDeactivating =
    isTelegramActive && (!hasValidId || !hasEnabledNotifications);
  const hasChanges = hasStateChanged();

  return (
    <div className="min-h-[400px] h-fit p-6"  >
      <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-sm p-6 border border-[#fbf4ff] dark:!border-[#3c3a3d]">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-[#9e68ba] mb-2 flex items-center">
            <Send className="ml-2" size={20} />
            {t("servicePageActivation.telegram.title")}
          </h3>
          <p className="text-gray-600 dark:!text-gray-400">
            {t("servicePageActivation.telegram.description")}
          </p>
        </div>

        {/* Service Status Indicator */}
        <div
          className={`mb-4 p-3 rounded-lg ${isTelegramActive
            ? "bg-green-50 border border-green-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
            : "bg-gray-50 border border-gray-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
            }`}
        >
          <div className="flex gap-x-2 items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${isTelegramActive
                ? "bg-green-500"
                : "bg-gray-400 dark:!bg-gray-500"
                }`}
            ></div>
            <span
              className={`text-sm font-medium ${isTelegramActive
                ? "text-green-700"
                : "text-gray-700 dark:!text-gray-300"
                }`}
            >
              {isTelegramActive
                ? t("servicePageActivation.telegram.status.active")
                : t("servicePageActivation.telegram.status.inactive")}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Telegram ID Input */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-[#9e68ba]">
                {t("servicePageActivation.telegram.form.telegramIdLabel")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div
                className={`w-fit flex items-center gap-x-2 px-2 py-1 rounded-full ${data.Idtelegram && /^\d{7,12}$/.test(data.Idtelegram)
                    ? "bg-green-100 dark:!bg-green-900"
                    : "bg-red-100 dark:!bg-red-900"
                  }`}
              >
                <span
                  className={`w-fit text-xs font-medium ${data.Idtelegram && /^\d{7,12}$/.test(data.Idtelegram)
                      ? "text-green-800 dark:!text-green-300"
                      : "text-red-800 dark:!text-red-300"
                    }`}
                >
                  {data.Idtelegram && /^\d{7,12}$/.test(data.Idtelegram)
                    ? t("servicePageActivation.telegram.form.validId")
                    : t("servicePageActivation.telegram.form.invalidId")}
                </span>
                <div className="flex items-center pl-3 pointer-events-none">
                  {data.Idtelegram &&
                    (/^\d{7,12}$/.test(data.Idtelegram) ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : (
                      <X className="h-5 w-5 text-red-500" />
                    ))}
                </div>
              </div>
            </div>
            <div className="relative">
              <input
                type="text"
                className={`w-full p-3 border ${!/^\d{7,12}$/.test(data.Idtelegram) && data.Idtelegram
                    ? "border-red-300 focus:ring-red-500 dark:!border-red-700"
                    : "border-gray-300 focus:ring-purple-200 dark:!border-[#3c3a3d]"
                  } rounded-lg bg-white dark:!bg-[#141414] focus:outline-none focus:ring-2 transition-colors pr-10 dark:!text-gray-200`}
                placeholder={t("servicePageActivation.telegram.form.telegramIdPlaceholder")}
                value={data.Idtelegram || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 12) {
                    setData({ ...data, Idtelegram: value });
                  }
                }}
              />
              {data.Idtelegram && (
                <button
                  type="button"
                  onClick={clearTelegramId}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:!bg-[#3c3a3d]"
                  title={t("servicePageActivation.telegram.form.clear")}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {!/^\d{7,12}$/.test(data.Idtelegram) && data.Idtelegram && (
              <p className="mt-2 text-sm text-red-600 dark:!text-red-400">
                {t("servicePageActivation.telegram.form.idError")}
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:!text-gray-400 flex items-start">
              <Info className="inline-block ml-1 mt-0.5 flex-shrink-0" size={14} />
              {t("servicePageActivation.telegram.form.idHint")}
            </p>
          </div>

          {/* Notification Types */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-[#9e68ba]">
                {t("servicePageActivation.telegram.form.notificationTypesLabel")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div
                className={`px-2 py-1 rounded-full text-xs font-medium ${hasEnabledNotifications
                    ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                    : "bg-gray-100 text-gray-800 dark:!bg-[#181919] dark:!text-gray-300"
                  }`}
              >
                {hasEnabledNotifications
                  ? t("servicePageActivation.telegram.form.atLeastOneType")
                  : t("servicePageActivation.telegram.form.noTypeSelected")}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {notificationItems.map((item) => (
                <div
                  key={item.key}
                  className={`p-3 rounded-lg border transition-colors cursor-pointer flex items-center ${data[item.key]
                      ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
                      : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#141414] dark:!border-[#3c3a3d]"
                    }`}
                  onClick={() => toggleNotificationType(item.key)}
                >
                  <div className="relative overflow-hidden inline-block w-10 ml-2 align-middle select-none">
                    <div
                      className={`cursor-pointer rounded-full w-10 h-5 transition-colors ${data[item.key]
                          ? "bg-purple-400"
                          : "bg-gray-300 dark:!bg-gray-600"
                        } flex items-center`}
                    >
                      <span
                        className={`block h-5 w-5 rounded-full bg-white dark:!bg-[#181919] shadow transform transition-transform duration-200 ease-in ${data[item.key] ? "translate-x-5" : "translate-x-0"
                          }`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center dark:!text-gray-200">
                    {item.icon}
                    <span className={data[item.key] ? "font-medium" : ""}>
                      {item.label}
                    </span>
                  </div>
                  <span className="mr-auto text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 dark:!bg-[#181919] dark:!text-gray-400">
                    {item.badge}
                  </span>
                </div>
              ))}
            </div>
            {!hasEnabledNotifications && (
              <p className="mt-2 text-sm text-amber-600 dark:!text-amber-400">
                {t("servicePageActivation.telegram.form.minimum")}
              </p>
            )}
          </div>

          {/* Tips Section */}
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100 dark:!bg-[#181919] dark:!border-[#3c3a3d]">
            <h4 className="text-lg font-medium text-blue-800 dark:!text-gray-200 mb-2 flex items-center">
              <Info size={18} className="ml-2" />
              {t("servicePageActivation.telegram.form.tips.title")}
            </h4>
            <ul className="text-sm text-gray-700 dark:!text-gray-400 space-y-2 mr-5 list-disc">
              <li>{t("servicePageActivation.telegram.form.tips.list.0")}</li>
              <li>{t("servicePageActivation.telegram.form.tips.list.1")}</li>
              <li>{t("servicePageActivation.telegram.form.tips.list.2")}</li>
              <li>{t("servicePageActivation.telegram.form.tips.list.3")}</li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !hasChanges}
            className={`px-6 py-3 ${!hasChanges
                ? "bg-gray-400"
                : isDeactivating
                  ? "bg-red-500 hover:bg-red-600"
                  : isFormValid
                    ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                    : "bg-gray-400"
              } text-white rounded-lg transition-colors flex items-center justify-center ${loading || !hasChanges ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin ml-2" size={18} />
                {isDeactivating ? t("servicePageActivation.upsell.button.disabling") : t("navbar.try.saving")}
              </>
            ) : (
              <>
                {!hasChanges ? (
                  <>
                    <Info className="ml-2" size={18} />
                    {t("servicePageActivation.telegram.form.submit.noChange")}
                  </>
                ) : isDeactivating ? (
                  <>
                    <X className="ml-2" size={18} />
                    {t("servicePageActivation.telegram.form.submit.deactivate")}
                  </>
                ) : isFormValid ? (
                  isTelegramActive ? (
                    <>
                      <Check className="ml-2" size={18} />
                      {t("servicePageActivation.telegram.form.submit.save")}
                    </>
                  ) : (
                    <>
                      <Check className="ml-2" size={18} />
                      {t("servicePageActivation.telegram.form.submit.activate")}
                    </>
                  )
                ) : (
                  <>
                    <AlertTriangle className="ml-2" size={18} />
                    {t("servicePageActivation.telegram.form.submit.completeForm")}
                  </>
                )}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
